<template>
  <div class="wechat-video-page">
    <section class="hero-card">
      <div class="hero-copy">
        <div class="hero-title-row">
          <div>
            <h1>视频号视频下载</h1>
            <p>在电脑微信里打开视频号卡片，检测到视频后即可保存到本机。</p>
          </div>
          <div class="status-row">
            <span class="status-label">本地助手</span>
            <span class="status-pill" :class="agentOnline ? 'online' : 'offline'">
              {{ agentOnline ? '已启动' : '未启动' }}
            </span>
            <template v-if="agentOnline">
              <span class="status-label">证书</span>
              <span class="status-pill" :class="certificateInstalled ? 'online' : 'warn'">
                {{ certificateInstalled ? '已安装' : '未安装' }}
              </span>
            </template>
          </div>
        </div>
      </div>
      <div class="hero-actions">
        <el-button :loading="checkingAgent" @click="checkAgent(true)">重新检测</el-button>
        <el-button
          v-if="!agentOnline"
          type="success"
          icon="el-icon-download"
          @click="downloadAgent"
        >
          下载 Windows 助手
        </el-button>
        <el-button
          v-if="agentOnline && !certificateInstalled"
          type="primary"
          :loading="certificateBusy === 'install'"
          @click="installCertificate"
        >
          安装本机证书
        </el-button>
      </div>
    </section>

    <section v-if="!agentOnline" class="panel-card offline-card">
      <div class="offline-main">
        <h2>请先启动本地助手</h2>
        <p>下载后解压，运行 <b>WeChatVideoBotAgent.exe</b>。助手启动后，本页面会自动连接。</p>
      </div>
      <div class="offline-actions">
        <el-button type="success" icon="el-icon-download" @click="downloadAgent">
          下载 Windows 助手
        </el-button>
        <el-button :loading="checkingAgent" @click="checkAgent(true)">我已启动，重新检测</el-button>
      </div>
    </section>

    <template v-else>
      <section v-if="!certificateInstalled" class="panel-card cert-card">
        <div class="cert-copy">
          <h2>首次使用需要安装本机证书</h2>
          <p>安装后请完全退出并重新打开微信，再在电脑微信里打开视频号卡片。</p>
        </div>
        <el-button
          type="primary"
          :disabled="!certificateAvailable"
          :loading="certificateBusy === 'install'"
          @click="installCertificate"
        >
          安装本机证书
        </el-button>
      </section>

      <section class="panel-card records-panel">
        <div class="panel-head">
          <div>
            <h2>视频记录</h2>
            <p>检测到 {{ records.length }} 条记录</p>
          </div>
          <el-button size="mini" :loading="recordsLoading" @click="refreshRecords(true)">
            刷新
          </el-button>
        </div>

        <div v-if="records.length === 0" class="empty-state">
          暂时还没有检测到视频记录。请先在电脑微信里手动点开一个视频号卡片。
        </div>

        <div v-else class="record-grid">
          <article v-for="record in records" :key="record.record_id" class="record-card">
            <div class="record-head">
              <div>
                <h3>视频号视频</h3>
                <p>{{ formatShortTime(record.created_at) }}</p>
              </div>
              <span class="status-pill" :class="recordStatusClass(record)">
                {{ recordStatusText(record) }}
              </span>
            </div>

            <div class="record-actions">
              <el-button
                type="primary"
                size="mini"
                :disabled="!record.media_url || record.status === 'downloading'"
                :loading="isRecordDownloading(record)"
                @click="downloadRecord(record)"
              >
                {{ record.status === 'downloaded' ? '重新下载' : '下载视频' }}
              </el-button>
              <el-button
                v-if="record.output_path"
                size="mini"
                @click="copyText(record.output_path)"
              >
                复制路径
              </el-button>
              <el-button size="mini" type="text" @click="toggleDetails(record)">
                {{ isDetailsOpen(record) ? '收起' : '详情' }}
              </el-button>
            </div>

            <div v-if="isDetailsOpen(record)" class="record-details">
              <div class="detail-row">
                <span>检测时间</span>
                <strong>{{ formatTime(record.created_at) }}</strong>
              </div>
              <div v-if="record.media_url" class="detail-row detail-row--url">
                <span>视频地址</span>
                <div class="url-line">
                  <strong :title="record.media_url">{{ compactUrl(record.media_url) }}</strong>
                  <button type="button" @click="copyText(record.media_url)">复制</button>
                </div>
              </div>
              <div v-if="record.output_path" class="detail-row detail-row--url">
                <span>保存位置</span>
                <div class="url-line">
                  <strong :title="record.output_path">{{ record.output_path }}</strong>
                  <button type="button" @click="copyText(record.output_path)">复制</button>
                </div>
              </div>
              <div v-if="record.error_message" class="detail-row">
                <span>错误信息</span>
                <strong>{{ record.error_message }}</strong>
              </div>
            </div>
          </article>
        </div>
      </section>
    </template>
  </div>
</template>

<script>
import axios from 'axios'

const AGENT_ORIGIN = 'http://127.0.0.1:8765'
const AGENT_DOWNLOAD_PATH = '/downloads/WeChatVideoBotAgent.zip'

const agentClient = axios.create({
  baseURL: AGENT_ORIGIN,
  timeout: 2500
})

function resolvePublicAssetUrl(path) {
  const assetPath = String(path || '').replace(/^\/+/, '')
  const pageUrl = window.location.href.split('#')[0]
  return new URL(assetPath, pageUrl).toString()
}

export default {
  name: 'WeChatVideoBot',
  data() {
    return {
      agentOnline: false,
      agentVersion: '',
      checkingAgent: false,
      recordsLoading: false,
      records: [],
      certificateStatus: {},
      certificateBusy: '',
      pollTimer: null,
      expandedRecordIds: {},
      downloadingRecordIds: {}
    }
  },
  computed: {
    agentDownloadUrl() {
      return resolvePublicAssetUrl(AGENT_DOWNLOAD_PATH)
    },
    certificateInstalled() {
      return !!(this.certificateStatus && this.certificateStatus.installed)
    },
    certificateAvailable() {
      return !!(this.certificateStatus && this.certificateStatus.available)
    }
  },
  mounted() {
    this.checkAgent(false)
    this.pollTimer = window.setInterval(() => {
      this.checkAgent(false)
    }, 2000)
  },
  beforeDestroy() {
    if (this.pollTimer) window.clearInterval(this.pollTimer)
  },
  methods: {
    async checkAgent(showToast) {
      if (this.checkingAgent) return
      this.checkingAgent = true
      try {
        const res = await agentClient.get('/health')
        const data = res.data || {}
        if (!data.ok) throw new Error('agent health check failed')
        this.agentOnline = true
        this.agentVersion = data.version || ''
        this.certificateStatus = data.certificate || this.certificateStatus || {}
        await this.refreshRecords(false)
        if (showToast) this.$message.success('已检测到本地助手')
      } catch (error) {
        this.agentOnline = false
        this.agentVersion = ''
        this.records = []
        this.certificateStatus = {}
        if (showToast) this.$message.warning('未检测到本地助手，请先下载安装并启动')
      } finally {
        this.checkingAgent = false
      }
    },
    async refreshRecords(showToast) {
      if (!this.agentOnline) return
      this.recordsLoading = true
      try {
        const res = await agentClient.get('/api/records')
        this.records = Array.isArray(res.data && res.data.records) ? res.data.records : []
        if (showToast) this.$message.success('已刷新')
      } catch (error) {
        if (showToast) this.$message.warning(this.extractMessage(error, '刷新失败'))
      } finally {
        this.recordsLoading = false
      }
    },
    async installCertificate() {
      this.certificateBusy = 'install'
      try {
        const res = await agentClient.post('/api/certificate/install')
        const data = res.data || {}
        this.certificateStatus = data.status || {}
        if (!data.ok) {
          this.$message.warning(data.message || '证书安装失败')
          return
        }
        this.$alert('证书已安装。请完全退出并重新打开微信。', '安装完成', {
          confirmButtonText: '知道了'
        })
      } catch (error) {
        this.$message.warning(this.extractMessage(error, '证书安装失败'))
      } finally {
        this.certificateBusy = ''
        await this.refreshCertificate()
      }
    },
    async refreshCertificate() {
      if (!this.agentOnline) return
      try {
        const res = await agentClient.get('/api/certificate')
        this.certificateStatus = (res.data && res.data.status) || {}
      } catch (error) {
        /* ignore */
      }
    },
    async downloadRecord(record) {
      const recordId = record && record.record_id
      if (!recordId) return
      this.$set(this.downloadingRecordIds, recordId, true)
      try {
        const res = await agentClient.post(`/api/records/${recordId}/download`)
        const data = res.data || {}
        if (!data.ok) {
          this.$message.warning(data.message || '启动下载失败')
          return
        }
        this.$message.success('已开始下载')
        await this.refreshRecords(false)
      } catch (error) {
        this.$message.warning(this.extractMessage(error, '启动下载失败'))
      } finally {
        this.$delete(this.downloadingRecordIds, recordId)
      }
    },
    downloadAgent() {
      window.open(this.agentDownloadUrl, '_blank')
    },
    toggleDetails(record) {
      const recordId = record && record.record_id
      if (!recordId) return
      if (this.expandedRecordIds[recordId]) {
        this.$delete(this.expandedRecordIds, recordId)
      } else {
        this.$set(this.expandedRecordIds, recordId, true)
      }
    },
    isDetailsOpen(record) {
      return !!(record && this.expandedRecordIds[record.record_id])
    },
    isRecordDownloading(record) {
      return !!(record && this.downloadingRecordIds[record.record_id])
    },
    recordStatusText(record) {
      if (!record) return '等待抓取'
      if (record.status === 'failed') return '下载失败'
      if (record.status === 'downloading') return '下载中'
      if (record.status === 'downloaded') return '已下载'
      if (record.media_url) return '可下载'
      return '等待抓取'
    },
    recordStatusClass(record) {
      if (!record) return 'warn'
      if (record.status === 'failed') return 'offline'
      if (record.status === 'downloading') return 'warn'
      if (record.status === 'downloaded' || record.media_url) return 'online'
      return 'warn'
    },
    formatTime(ts) {
      if (!ts) return '-'
      return new Date(ts * 1000).toLocaleString()
    },
    formatShortTime(ts) {
      if (!ts) return '-'
      return new Date(ts * 1000).toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    compactUrl(value) {
      const text = String(value || '').trim()
      if (!text) return '-'
      try {
        const url = new URL(text)
        const path = url.pathname.length > 30 ? `${url.pathname.slice(0, 30)}...` : url.pathname
        return `${url.host}${path}`
      } catch (error) {
        return text.length > 48 ? `${text.slice(0, 48)}...` : text
      }
    },
    async copyText(value) {
      const text = String(value || '')
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(text)
        } else {
          const textarea = document.createElement('textarea')
          textarea.value = text
          textarea.style.position = 'fixed'
          textarea.style.opacity = '0'
          document.body.appendChild(textarea)
          textarea.select()
          document.execCommand('copy')
          document.body.removeChild(textarea)
        }
        this.$message.success('已复制')
      } catch (error) {
        this.$message.warning('复制失败')
      }
    },
    extractMessage(error, fallback) {
      return (error.response && error.response.data && error.response.data.message) || error.message || fallback
    }
  }
}
</script>

<style lang="less" scoped>
.wechat-video-page {
  display: grid;
  gap: 18px;
  color: var(--text-primary, #303133);
}

.hero-card,
.panel-card,
.record-card {
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.98), rgba(246, 249, 252, 0.96));
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 18px;
  box-shadow: 0 14px 34px rgba(15, 23, 42, 0.08);
}

.hero-card {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 18px;
  padding: 22px;
}

.hero-title-row {
  display: flex;
  align-items: center;
  gap: 18px;
  flex-wrap: wrap;
}

.hero-copy h1,
.panel-head h2,
.offline-main h2,
.cert-copy h2 {
  margin: 0;
  color: var(--text-primary, #303133);
}

.hero-copy h1 {
  font-size: 30px;
  line-height: 1.15;
}

.hero-copy p,
.panel-head p,
.offline-main p,
.cert-copy p {
  margin: 8px 0 0;
  color: var(--text-regular, #606266);
  line-height: 1.6;
}

.status-row,
.hero-actions,
.offline-actions,
.record-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.status-label {
  font-size: 13px;
  color: var(--text-secondary, #909399);
}

.status-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
}

.status-pill.online {
  color: #15803d;
  background: rgba(34, 197, 94, 0.14);
}

.status-pill.offline {
  color: #b91c1c;
  background: rgba(239, 68, 68, 0.12);
}

.status-pill.warn {
  color: #9a5b00;
  background: rgba(245, 158, 11, 0.14);
}

.panel-card {
  padding: 20px;
}

.offline-card,
.cert-card,
.panel-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
}

.records-panel {
  display: grid;
  gap: 16px;
}

.empty-state {
  width: 100%;
  padding: 28px 16px;
  border: 1px dashed rgba(15, 23, 42, 0.16);
  border-radius: 16px;
  color: var(--text-secondary, #909399);
  text-align: center;
  white-space: nowrap;
  overflow-x: auto;
}

.record-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 14px;
}

.record-card {
  padding: 16px;
}

.record-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.record-head h3 {
  margin: 0;
  font-size: 18px;
  color: var(--text-primary, #303133);
}

.record-head p {
  margin: 6px 0 0;
  font-size: 12px;
  color: var(--text-secondary, #909399);
}

.record-actions {
  margin-top: 14px;
}

.record-details {
  display: grid;
  gap: 9px;
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px solid rgba(15, 23, 42, 0.08);
}

.detail-row {
  display: grid;
  grid-template-columns: 72px minmax(0, 1fr);
  gap: 10px;
  font-size: 13px;
  line-height: 1.45;
}

.detail-row span {
  color: var(--text-secondary, #909399);
}

.detail-row strong {
  min-width: 0;
  font-weight: 500;
  color: var(--text-regular, #606266);
  word-break: break-word;
}

.url-line {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.url-line strong {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.url-line button {
  flex: 0 0 auto;
  border: 1px solid rgba(15, 23, 42, 0.12);
  border-radius: 8px;
  background: transparent;
  color: var(--text-regular, #606266);
  padding: 4px 8px;
  font-size: 12px;
  cursor: pointer;
}

@media (max-width: 768px) {
  .hero-card,
  .offline-card,
  .cert-card,
  .panel-head {
    align-items: stretch;
    flex-direction: column;
  }

  .hero-actions,
  .offline-actions {
    align-items: stretch;
  }

  .hero-actions .el-button,
  .offline-actions .el-button,
  .cert-card .el-button {
    width: 100%;
    margin-left: 0;
  }

  .empty-state {
    white-space: normal;
  }

  .record-grid {
    grid-template-columns: 1fr;
  }
}
</style>

<style lang="less">
.dark-mode .wechat-video-page {
  .hero-card,
  .panel-card,
  .record-card {
    background: var(--bg-card) !important;
    border-color: var(--border-color) !important;
    box-shadow: 0 14px 34px var(--shadow-color) !important;
  }

  .record-details {
    border-top-color: var(--border-color) !important;
  }

  .empty-state,
  .url-line button {
    border-color: var(--border-color) !important;
  }
}
</style>
